import React from 'react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@asad/crane-ui'

import { useAppDispatch } from '@/hooks/reduxHooks'
import { doSendEmailMfaForSetup } from '@/store/slices/auth-slice'

import { MFA_RESEND_COOLDOWN_SECONDS, mapMfaError } from '../mfa-utils'
import { useCountdown } from './useCountdown'

type UseMfaEmailSetupOptions = {
  onFirstSend?: () => void
}

type UseMfaEmailSetupReturn = {
  setupEmailMfaToken: string
  emailOtpCode: string
  setEmailOtpCode: (v: string) => void
  emailSending: boolean
  emailSent: boolean
  emailError: string
  setEmailError: (v: string) => void
  resendSecondsLeft: number
  pickEmail: () => Promise<void>
  resendSetupEmail: () => Promise<void>
  reset: () => void
}

export const useMfaEmailSetup = ({ onFirstSend }: UseMfaEmailSetupOptions = {}): UseMfaEmailSetupReturn => {
  const [setupEmailMfaToken, setSetupEmailMfaToken] = React.useState('')
  const [emailOtpCode, setEmailOtpCode] = React.useState('')
  const [emailSending, setEmailSending] = React.useState(false)
  const [emailSent, setEmailSent] = React.useState(false)
  const [emailError, setEmailError] = React.useState('')
  const [resendSecondsLeft, setResendSecondsLeft] = useCountdown()

  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const toast = useToast()

  const onFirstSendRef = React.useRef(onFirstSend)
  React.useEffect(() => {
    onFirstSendRef.current = onFirstSend
  })

  const sendEmail = async (): Promise<boolean> => {
    setEmailSending(true)
    setEmailError('')
    setEmailSent(false)
    try {
      const res = await dispatch(doSendEmailMfaForSetup(i18n.language)).unwrap()
      setSetupEmailMfaToken(res.emailMfaToken)
      setEmailSent(true)
      setResendSecondsLeft(MFA_RESEND_COOLDOWN_SECONDS)
      toast.success(t('mfaVerify.emailSent'))
      return true
    } catch (error) {
      setEmailError(mapMfaError(error, t))
      return false
    } finally {
      setEmailSending(false)
    }
  }

  const pickEmail = async () => {
    const success = await sendEmail()
    if (success) onFirstSendRef.current?.()
  }

  const resendSetupEmail = async () => {
    await sendEmail()
  }

  const reset = () => {
    setSetupEmailMfaToken('')
    setEmailOtpCode('')
    setEmailSending(false)
    setEmailSent(false)
    setEmailError('')
    setResendSecondsLeft(0)
  }

  return {
    setupEmailMfaToken,
    emailOtpCode,
    setEmailOtpCode,
    emailSending,
    emailSent,
    emailError,
    setEmailError,
    resendSecondsLeft,
    pickEmail,
    resendSetupEmail,
    reset,
  }
}
